import getConfig from 'next/config';
import type { ElementType } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useCookiesWithOptions } from '@/hooks/useCookiesWithOptions';
import type { Values } from '@/types/Values';
import { Button, ButtonVariants } from '@/ui/Button';
import { Inline } from '@/ui/Inline';
import { Link } from '@/ui/Link';
import { List } from '@/ui/List';
import { cn } from '@/ui/shadcn/utils';
import { Stack } from '@/ui/Stack';
import { Text } from '@/ui/Text';
import { colors, getValueFromTheme } from '@/ui/theme';
import {
  openZendeskWidget,
  showZendeskWidget,
  ZendeskWidget,
} from '@/ui/ZendeskWidget';

import { SupportedLanguages } from '../i18n';
import { NewsletterSignupForm } from './dashboard/NewsletterSingupForm';

const getValueForPage = getValueFromTheme('loginPage');

type FlandersLogoProps = {
  color: string;
  width: string;
};

const FlandersLogo = ({ color, width }: FlandersLogoProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      viewBox="0 0 197.402 83.169"
    >
      <g fill={color}>
        <path d="m77.482 21.289-6.38 18.517H67.31l-6.38-18.517h3.433l4.844 14.061 4.844-14.061h3.431zm2.219-1.523 2.823-.277h.415v20.04h-3.238V19.766zm17.702 20.04a3.91 3.91 0 0 1-1.605-.318c-.54-.221-1.08-.595-1.62-1.189-.567.429-1.065.803-1.661 1.065-.609.276-1.287.442-2.214.442a6.384 6.384 0 0 1-2.104-.346c-.706-.249-1.356-.664-1.841-1.343-.484-.678-.789-1.619-.789-2.892 0-1.26.304-2.271 1.079-2.948.775-.678 2.035-1.052 3.958-1.052.775 0 1.674 0 2.948.11v-.72c0-.996-.318-1.688-.831-2.104-.512-.415-1.204-.581-1.979-.581-.609 0-1.287.111-1.951.291-.665.18-1.329.415-1.896.692l-.761-2.823c1.508-.72 3.432-1.218 5.245-1.218 1.425 0 2.754.305 3.75 1.052.983.748 1.605 1.924 1.605 3.682v5.342c0 .679.222 1.163.609 1.495.374.332.899.525 1.522.65l-1.464 2.713zm-3.861-6.089c-1.509 0-2.685-.028-3.488.166-.803.193-1.246.608-1.246 1.453 0 1.177.844 1.827 2.021 1.827.401 0 1.08-.083 1.661-.443.581-.359 1.052-.982 1.052-2.131v-.872zm17.993 6.089a3.92 3.92 0 0 1-1.605-.318c-.539-.221-1.078-.595-1.619-1.189-.566.429-1.064.803-1.66 1.065-.609.276-1.287.442-2.214.442-.664 0-1.397-.097-2.104-.346s-1.356-.664-1.841-1.343c-.484-.678-.789-1.619-.789-2.892 0-1.26.305-2.271 1.08-2.948.774-.678 2.034-1.052 3.958-1.052.775 0 1.675 0 2.947.11v-.72c0-.996-.316-1.688-.83-2.104-.512-.415-1.204-.581-1.978-.581-.609 0-1.287.111-1.952.291-.664.18-1.328.415-1.896.692l-.762-2.823c1.509-.72 3.433-1.218 5.246-1.218 1.425 0 2.753.305 3.75 1.052.982.748 1.605 1.924 1.605 3.682v5.342c0 .679.223 1.163.609 1.495.373.332.9.525 1.521.65l-1.466 2.713zm-3.861-6.089c-1.508 0-2.684-.028-3.487.166-.803.193-1.245.608-1.245 1.453 0 1.177.844 1.827 2.021 1.827.401 0 1.079-.083 1.661-.443.58-.359 1.051-.982 1.051-2.131v-.872zm7.269-8.553h2.74l.318 1.121a8.415 8.415 0 0 1 4.582-1.398c1.369 0 2.684.374 3.639 1.204.955.831 1.578 2.118 1.578 3.903v9.535h-3.238v-8.581c0-1.065-.387-1.812-.982-2.283s-1.426-.664-2.27-.664a6.055 6.055 0 0 0-3.129.886V39.53h-3.238V25.164zm28.362 14.365h-2.449l-.58-1.079c-1.107.83-2.271 1.355-3.861 1.355-2.062 0-3.6-.83-4.609-2.104s-1.508-2.989-1.508-4.761c0-2.712.816-4.733 2.172-6.076 1.357-1.329 3.266-1.979 5.48-1.979.65 0 1.357.042 2.117.139v-5.259l2.74-.277h.498v20.041zm-3.239-11.321a9.7 9.7 0 0 0-2.297-.305c-1.232 0-2.27.36-2.99 1.107-.719.748-1.135 1.854-1.135 3.349 0 1.094.305 2.159.887 2.976s1.439 1.356 2.588 1.356c.982 0 2.076-.347 2.947-.886v-7.597zm17.358 7.599v3.142c-.65.318-1.342.512-2.047.65-.721.139-1.426.207-2.035.207-1.992 0-3.834-.622-5.189-1.854-1.357-1.231-2.215-3.113-2.215-5.604 0-2.422.803-4.277 2.021-5.55a6.14 6.14 0 0 1 4.441-1.91c.457 0 1.273.056 2.117.346a5.056 5.056 0 0 1 2.465 1.799c.691.955 1.176 2.366 1.176 4.359v2.159h-8.719c.262 1.162.857 1.951 1.633 2.449.762.498 1.688.706 2.615.706 1.219 0 2.506-.346 3.613-.941l.124.042zm-2.227-4.817c-.098-1.052-.443-1.799-.941-2.297a2.49 2.49 0 0 0-1.758-.692c-.664 0-1.342.221-1.896.706-.566.499-1.023 1.246-1.203 2.284h5.798zm13.883-2.256c-.816-.498-1.619-.72-2.436-.72-1.051 0-2.02.388-2.893 1.052v10.463h-3.238V25.164h2.74l.332 1.176c.996-.844 2.215-1.453 3.654-1.453.387 0 .928.028 1.453.125s1.037.235 1.412.443l-1.024 3.279zm12.375 7.073v3.142c-.65.318-1.342.512-2.047.65-.721.139-1.426.207-2.035.207-1.992 0-3.834-.622-5.189-1.854-1.357-1.231-2.215-3.113-2.215-5.604 0-2.422.803-4.277 2.021-5.55a6.14 6.14 0 0 1 4.441-1.91c.457 0 1.273.056 2.117.346a5.056 5.056 0 0 1 2.465 1.799c.691.955 1.176 2.366 1.176 4.359v2.159h-8.719c.262 1.162.857 1.951 1.633 2.449.762.498 1.688.706 2.615.706 1.219 0 2.506-.346 3.613-.941l.124.042zm-2.226-4.817c-.098-1.052-.443-1.799-.941-2.297a2.49 2.49 0 0 0-1.758-.692c-.664 0-1.342.221-1.896.706-.566.499-1.023 1.246-1.203 2.284h5.798zm5.316-5.826h2.74l.318 1.121a8.415 8.415 0 0 1 4.582-1.398c1.369 0 2.684.374 3.639 1.204.955.831 1.578 2.118 1.578 3.903v9.535h-3.238v-8.581c0-1.065-.387-1.812-.982-2.283s-1.426-.664-2.27-.664a6.055 6.055 0 0 0-3.129.886V39.53h-3.238V25.164zM72.567 52.175l-2.805 8.157h-1.627l-2.797-8.157h1.257l2.357 6.86 2.357-6.86h1.258zm7.149 4.386h-5.084c.063.942.393 1.642.872 2.105.479.464 1.124.691 1.823.691.731 0 1.454-.204 2.2-.581l.024.008v1.123c-.849.417-1.587.582-2.342.582-1.014 0-1.965-.338-2.656-1.045-.691-.708-1.131-1.784-1.131-3.293 0-1.163.267-2.208.841-2.947.565-.738 1.438-1.187 2.664-1.187.841 0 1.422.213 1.839.551.409.338.652.801.786 1.312.133.511.165 1.068.165 1.579v1.102zm-1.195-1.101c-.008-.629-.094-1.202-.338-1.627-.244-.424-.644-.684-1.281-.684-.621 0-1.116.244-1.493.652-.377.408-.621.99-.723 1.658h3.835zm3.37-3.285h.817l.283.928c.605-.699 1.25-1.085 2.004-1.085.196 0 .44.024.684.071s.479.126.668.212l-.424 1.093a2.935 2.935 0 0 0-1.124-.252c-.715 0-1.273.456-1.69 1.093v6.098h-1.218v-8.158zm5.978-3.065 1.14-.157h.079v3.78a3.33 3.33 0 0 1 2.098-.716c1.068 0 1.949.456 2.562 1.203.613.754.959 1.799.959 2.985 0 1.155-.322 2.225-.998 3.002-.676.786-1.706 1.281-3.112 1.281-1.014 0-1.973-.268-2.727-.582V49.11zm1.218 10.012c.534.212 1.061.251 1.595.251.817 0 1.517-.307 2.02-.872.495-.558.786-1.375.786-2.404 0-.81-.181-1.54-.582-2.075-.401-.534-1.014-.872-1.886-.872-.817 0-1.517.291-1.933.888v5.084zm13.169-2.561h-5.085c.063.942.394 1.642.873 2.105s1.123.691 1.823.691c.73 0 1.453-.204 2.2-.581l.023.008v1.123c-.849.417-1.588.582-2.342.582-1.014 0-1.965-.338-2.656-1.045-.691-.708-1.132-1.784-1.132-3.293 0-1.163.268-2.208.841-2.947.566-.738 1.438-1.187 2.664-1.187.841 0 1.423.213 1.839.551.409.338.652.801.786 1.312s.165 1.068.165 1.579v1.102zm-1.194-1.101c-.008-.629-.095-1.202-.338-1.627-.244-.424-.645-.684-1.281-.684-.621 0-1.116.244-1.493.652s-.621.99-.723 1.658h3.835zm8.626 1.101h-5.084c.062.942.393 1.642.872 2.105s1.124.691 1.823.691c.73 0 1.454-.204 2.2-.581l.023.008v1.123c-.849.417-1.587.582-2.342.582-1.014 0-1.964-.338-2.655-1.045-.692-.708-1.132-1.784-1.132-3.293 0-1.163.267-2.208.841-2.947.565-.738 1.438-1.187 2.664-1.187.841 0 1.422.213 1.839.551.408.338.652.801.785 1.312.134.511.165 1.068.165 1.579v1.102zm-1.195-1.101c-.008-.629-.094-1.202-.338-1.627-.243-.424-.645-.684-1.28-.684-.621 0-1.116.244-1.493.652-.378.408-.621.99-.724 1.658h3.835zm2.96-6.35 1.132-.157h.079v11.379h-1.211V49.11zm9.933 11.222h-.771l-.252-.723c-.62.534-1.352.872-2.294.872-1.069 0-1.949-.495-2.562-1.289-.612-.785-.958-1.87-.958-3.057 0-1.155.33-2.177 1.006-2.923.676-.747 1.697-1.195 3.104-1.195a7.75 7.75 0 0 1 1.509.165V49.11l1.14-.157h.079v11.379zm-1.219-6.876a4.442 4.442 0 0 0-1.595-.322c-.817 0-1.517.26-2.012.778-.503.519-.794 1.296-.794 2.326 0 .809.181 1.587.581 2.16.401.574 1.015.951 1.887.951.817 0 1.501-.283 1.933-.888v-5.005zm3.731-3.505a.844.844 0 0 1 1.69 0 .797.797 0 0 1-.252.582.848.848 0 0 1-.597.251.818.818 0 0 1-.59-.251.796.796 0 0 1-.251-.582zm.236 2.224h1.21v8.157h-1.21v-8.157zm3.968 0h.816l.229.755c.338-.22.746-.448 1.179-.621.432-.173.88-.291 1.265-.291.778 0 1.493.197 2.027.684.527.487.857 1.258.857 2.413v5.218h-1.219v-5.147c0-.785-.204-1.296-.519-1.61-.322-.314-.77-.433-1.265-.433-.378 0-.763.103-1.132.252s-.707.346-1.021.534v6.404h-1.218v-8.158zm12.02 6.452c1.123 0 1.948.259 2.491.684.534.424.801 1.006.801 1.611 0 .888-.542 1.682-1.336 2.247a5.023 5.023 0 0 1-2.86.919c-.935 0-1.689-.29-2.208-.762a2.432 2.432 0 0 1-.81-1.839c0-.66.354-1.446.975-2.074-.22-.126-.385-.339-.495-.582s-.173-.503-.173-.73c0-.44.204-.943.59-1.423a2.258 2.258 0 0 1-.503-.888 3.476 3.476 0 0 1-.173-1.053c0-.778.306-1.454.841-1.941.534-.487 1.288-.778 2.177-.778.604 0 1.139.134 1.579.378.346-.26.738-.472 1.101-.613a2.79 2.79 0 0 1 .981-.212l.362 1.029c-.433.07-.998.251-1.533.589.307.44.472.951.472 1.517 0 .778-.314 1.493-.849 2.02-.542.527-1.305.857-2.192.857a3.398 3.398 0 0 1-1.509-.354c-.181.229-.275.456-.275.668 0 .409.354.731 1.006.731h1.54zm-2.004 1.045c-.44.503-.724 1.14-.724 1.705 0 .479.134.896.448 1.187.314.291.81.464 1.532.464.817 0 1.517-.268 2.02-.652.495-.377.778-.88.778-1.375 0-.605-.354-.936-.92-1.124-.573-.181-1.359-.204-2.216-.204h-.918zm1.288-6.569c-.604 0-1.068.228-1.383.55s-.472.747-.472 1.155.165.833.479 1.155.778.542 1.399.542c.604 0 1.061-.22 1.367-.542.307-.314.455-.73.455-1.14 0-.408-.157-.841-.463-1.171-.312-.33-.776-.549-1.382-.549zm21.136-.928-2.586 8.157h-1.509l-1.814-6.208-1.815 6.208h-1.517l-2.578-8.157h1.179l2.153 6.813 2.02-6.813h1.116l2.02 6.813 2.161-6.813h1.17zm7.155 4.386h-5.084c.062.942.393 1.642.872 2.105s1.124.691 1.823.691c.73 0 1.454-.204 2.2-.581l.023.008v1.123c-.849.417-1.587.582-2.342.582-1.014 0-1.964-.338-2.655-1.045-.692-.708-1.132-1.784-1.132-3.293 0-1.163.267-2.208.841-2.947.565-.738 1.438-1.187 2.664-1.187.841 0 1.422.213 1.839.551.408.338.652.801.785 1.312.134.511.165 1.068.165 1.579v1.102zm-1.194-1.101c-.008-.629-.094-1.202-.338-1.627-.243-.424-.645-.684-1.28-.684-.621 0-1.116.244-1.493.652-.378.408-.621.99-.724 1.658h3.835zm3.37-3.285h.817l.282.928c.605-.699 1.25-1.085 2.004-1.085.197 0 .44.024.685.071.243.047.479.126.668.212l-.425 1.093a2.935 2.935 0 0 0-1.124-.252c-.715 0-1.272.456-1.689 1.093v6.098h-1.218v-8.158zm11.651 8.314c-.408-.023-.888-.283-1.367-.66-.479-.369-.982-.872-1.422-1.383a16.258 16.258 0 0 1-1.147-1.438 6.163 6.163 0 0 1-.354-.526 1.818 1.818 0 0 1-.165-.291v4.142h-1.218V49.165l1.139-.212h.079v7.088l3.237-3.866h1.556l-3.315 3.945c.039.181.298.558.676.998.377.448.864.959 1.367 1.391.503.433 1.021.778 1.438.904l-.504 1.076zm1.044-8.314h.903v-1.461l1.14-.213h.079v1.674h2.263v1.132h-2.263v4.008c0 .746.165 1.249.424 1.579.26.33.629.472 1.045.472.472 0 .998-.181 1.532-.487l.33 1.116c-.691.314-1.375.495-2.012.495-.715 0-1.336-.22-1.799-.73-.464-.503-.739-1.305-.739-2.444v-4.008h-.903v-1.133zM37.088 0l28.238 83.02-.402.137L36.686.137zM36.863 41.409c-2.037-1.555-2.991.012-4.272-.092-1.084-.086-2.1-1.836-2.938-1.418-1.601.814.642 3.695 1.615 4.283.866.52 1.958.998 2.22 1.119 1.287.602 1.799 1.512 1.961 2.934.041.326 0 1.07-.08 1.416-.587 2.492-4.953 4.756-7.46 3.043-1.181-.809-2.259-1.854-2.632-3.703-.675-3.275-3.039-5.684-3.797-8.889-.461-1.947-.802-4.002-1.311-5.961-.517-2.025-1.049-4.031-1.473-5.871-.387-1.711-1.2-4.268-1.789-5.825-2.395-6.252-3.477-5.798-3.477-5.798s.872 1.735 4.043 17.059c.131.627 1.283 7.377 2.125 9.6.301.797.826 2.498 1.172 3.25 1.009 2.195 3.672 5.523 3.848 8.523.102 1.734.299 3.17.34 4.482.025.385.195 1.949.445 2.645.65 1.83 5.9 7.734 11.474 7.734v-4.475c-5.538 0-10.755-3.104-10.884-3.43-.08-.146.288-2.264.663-3.205.726-1.822 2.094-3.488 4.5-3.666 2.683-.193 4.302.586 5.721.586l-.014-14.341zM12.813 25.17c-.203 3.351-5.389 7.933-7.014 10.87-.796 1.445-1.769 3.926-2.02 5.363-.689 3.998.186 6.482 1.16 8.822 1.716 4.113-.232 5.625 1.109 4.791 1.661-1.354 1.408-4.51 1.286-6.48-.097-1.6-.204-3.418.038-5.324.461-3.566 2.62-7.395 4.464-10.07 2.19-3.192 1.395-6.493.977-7.972m1.045 9.517s.553 2.654-1.789 9.48c-6.125 17.854 5.575 19.545 8.906 23.742 0 0 1.289-1.818-4.018-7.639-1.938-2.127-3.686-6.812-2.163-13.506 2.203-9.691-.936-12.077-.936-12.077M2.276 22.981c-.28-1.055-.365-1.998-.304-2.84.283-4.195 4.093-5.894 5.042-6.519 0 0 1.713-1.173 1.934-2.357 0 0 1.754 4.415-2.268 6.944-2.041 1.286-3.428 2.792-4.404 4.772m9.607-4.054c.217.426 1.796 2.737-4.671 8.074-6.454 5.33-4.438 9.141-4.438 9.141s-6.89-3.832 1.047-10.453c7.934-6.615 6.308-7.957 6.308-7.957s1.135-.055 1.754 1.195m8.395 2.043c1.117.146 1.828 3.631 4.412 4.389 1.9.555 3.92.254 4.368 1.396-.872.463-.185 1.996.692 1.745.724-2.036 1.088-9.905-9.472-7.53zm3.48 1.568c.094-.25.229-.049.512-.227.302-.285.611-.753 1.102-.909.352-.108.834-.058 1.163.069.219.055.086.617-.078.762-.35.258-1.382-.167-1.382.606.043 1.269 1.715-.04 2.589-.004.426 3.096-4.666 2.244-3.906-.297z" />
      </g>
    </svg>
  );
};

const FooterVariants = {
  DEFAULT: 'default',
  LOGIN: 'login',
};

type LanguageSwitcherButtonProps = {
  footerVariant: Values<typeof FooterVariants>;
} & any;

const LanguageSwitcherButton = ({
  footerVariant,
  ...props
}: LanguageSwitcherButtonProps) => {
  return (
    <Button
      {...props}
      variant={ButtonVariants.UNSTYLED}
      css={`
        text-decoration: underline;
        color: ${footerVariant === FooterVariants.LOGIN
          ? getValueForPage('footer.linkColor')
          : '#005c7c'};
        &:hover {
          color: #222;
        }
      `}
    />
  );
};

type FooterLinkProps = {
  footerVariant: Values<typeof FooterVariants>;
} & any;

const FooterLink = ({ footerVariant, ...props }: FooterLinkProps) => (
  <Link
    {...props}
    className={cn(
      'tw:underline tw:hover:text-foreground',
      footerVariant === FooterVariants.LOGIN
        ? 'tw:text-secondary'
        : 'tw:text-[#005c7c]',
    )}
  />
);

type Props = {
  isProfileLinkVisible?: boolean;
  isNewsletterSignupFormVisible?: boolean;
  isZendeskWidgetVisible?: boolean;
  variant?: Values<typeof FooterVariants>;
  wrapper?: ElementType;
  onChangeLanguage?: (
    language: Values<typeof SupportedLanguages>,
  ) => () => Promise<boolean>;
};

const Footer = ({
  wrapper: Wrapper = (props) => (
    <Inline width="100%" justifyContent="space-between" {...props} />
  ),
  onChangeLanguage,
  isProfileLinkVisible = false,
  isNewsletterSignupFormVisible = false,
  isZendeskWidgetVisible = false,
  variant = FooterVariants.DEFAULT,
  ...props
}: Props) => {
  const { t, i18n } = useTranslation();
  const { setCookie } = useCookiesWithOptions(['udb-language']);
  const { publicRuntimeConfig } = getConfig();

  const defaultHandleChangeLanguage =
    (language: Values<typeof SupportedLanguages>) => () => {
      setCookie('udb-language', language);
    };

  const handleChangeLanguage = onChangeLanguage ?? defaultHandleChangeLanguage;

  const footerLinks = [
    {
      label: 'footer.contact',
      href: 'https://helpdesk.publiq.be/hc/nl/categories/360001783039-UiTdatabank',
    },
    {
      label: 'footer.about',
      href: 'http://www.publiq.be/nl/project/uitdatabank',
    },
    {
      label: 'footer.dev',
      href: 'https://docs.publiq.be/',
    },
    {
      label: 'footer.legal',
      href: `http://www.publiq.be/${i18n.language}/${t('footer.legal_path')}`,
    },
    {
      label: 'footer.privacy',
      href: 'http://www.publiq.be/nl/privacy-uitdatabank',
    },
  ];

  return (
    <Stack spacing={5} width="100%">
      {isZendeskWidgetVisible && (
        <Inline
          spacing={4}
          fontSize="1.2rem"
          paddingTop={5}
          alignItems="center"
        >
          <ZendeskWidget />
          <Trans i18nKey="dashboard.newsletter.questions_or_feedback">
            {i18n.language === 'nl' ? (
              <Button
                variant={ButtonVariants.LINK}
                onClick={() => {
                  openZendeskWidget();
                  showZendeskWidget();
                }}
              />
            ) : (
              <Link paddingX={2.5} href={`mailto:${t('error.email')}`}></Link>
            )}
          </Trans>
        </Inline>
      )}
      {isNewsletterSignupFormVisible && i18n.language === 'nl' && (
        <NewsletterSignupForm />
      )}

      <Wrapper>
        <Stack alignItems="flex-start">
          <List alignItems={{ xs: 'center' }}>
            {footerLinks.map((link) => (
              <List.Item key={link.label}>
                <FooterLink footerVariant={variant} href={link.href}>
                  {t(link.label)}
                </FooterLink>
              </List.Item>
            ))}
            {isProfileLinkVisible && (
              <List.Item>
                <FooterLink
                  footerVariant={variant}
                  href={`${publicRuntimeConfig.uitidProfileUrl}/${i18n.language}/profile`}
                >
                  {t('footer.profile')}
                </FooterLink>
              </List.Item>
            )}
          </List>
        </Stack>
        <Stack
          spacing={3}
          alignItems={{ default: 'flex-end', xs: 'center' }}
          justifyContent="flex-start"
        >
          <Inline as="p" spacing={2}>
            <Text
              css={`
                color: ${variant === FooterVariants.LOGIN
                  ? getValueForPage('footer.linkColor')
                  : 'inherit'};
              `}
            >
              {t('footer.by')}
            </Text>
            <FooterLink footerVariant={variant} href="http://www.publiq.be">
              publiq vzw
            </FooterLink>
          </Inline>
          <FlandersLogo color={colors.text} width="150" />
          <Inline>
            {Object.values(SupportedLanguages).map(
              (supportedLanguage, index) => {
                return (
                  <LanguageSwitcherButton
                    key={index}
                    onClick={handleChangeLanguage(supportedLanguage)}
                    footerVariant={variant}
                  >
                    {t(`footer.${supportedLanguage}`)}
                  </LanguageSwitcherButton>
                );
              },
            )}
          </Inline>
        </Stack>
      </Wrapper>
    </Stack>
  );
};

export { Footer, FooterVariants };
